import { Domain, User } from '@microsoft/microsoft-graph-types-beta';
import { Classification, EventRule, MicrosoftEvent, PredicateKeys, Predicates } from '../interfaces/meta.interface';
import workloads from '../workloads';
import {
    isIPInNetwork,
    isIPPublic,
    isIpInNetworks,
    ParsedNetwork,
    parseNetworks,
    isPointInCircle,
    isSystemUser,
    normaliseDomain,
    readForwardingRecipients,
} from './util';
import { PRIVILEGED_ROLES } from '../../../stolos/src/privileged-roles';
import {
    actorObjectId,
    isWorkloadIdentityActor,
    MICROSOFT_FIRST_PARTY_FLOOR,
    WorkloadIdentityContext,
} from './workload-identities';

/** Lowercased template ids and display names of the privileged directory roles, from the one shared list. */
const PRIVILEGED_TEMPLATE_IDS = new Set(PRIVILEGED_ROLES.map((role) => role.templateId.toLowerCase()));
const PRIVILEGED_ROLE_NAMES = new Set(PRIVILEGED_ROLES.map((role) => role.name.toLowerCase()));

function DefaultClassification(event: MicrosoftEvent): Classification {
    return {
        title: `${event.workload}: ${event.operation}`,
        description: `${event.workload}: ${event.operation}`,
        longDescription: `This "${event.operation}" event is considered low risk.`,
        severity: 'info',
    };
}

function ClassificationNotDefined(event: MicrosoftEvent): boolean {
    return (
        !workloads[event.workload] ||
        !workloads[event.workload][event.record_type] ||
        !workloads[event.workload][event.record_type]![event.operation]
    );
}

/**
 * An instant as epoch milliseconds, from whichever spelling a caller holds it in.
 *
 * A string is a Postgres `timestamptz` or an M365 timestamp (the two spellings the rest of this function
 * normalises). A `Date` or a number is accepted as well, although {@link EventRule} types `start` / `end` as strings,
 * because the Data API client hands back `Date` objects for a timestamp column unless its `deserializeDate` option is
 * switched off, and the clients in the repo differ on that (KRAK-393: the ingest loaded rules through one that returns
 * `Date`s, every rule with a window threw `value.replace is not a function`, and the caller swallowed it). Accepting the
 * shape here means the classifier cannot be crashed by it again, whatever loaded the rules.
 *
 * @param value the instant: a timestamp string, a `Date`, or epoch milliseconds
 * @returns epoch milliseconds, or `NaN` when the string is not a timestamp
 */
function parseTimestampMs(value: string | Date | number): number {
    if (value instanceof Date) {
        return value.getTime();
    }

    if (typeof value === 'number') {
        return value;
    }

    // Postgres timestamptz uses a space between date and time; ISO 8601 needs `T`.
    let normalised = value.replace(' ', 'T');
    // V8 `Date.parse` rejects bare `+HH` after a `T` separator; expand to `+HH:00`.
    normalised = normalised.replace(/([+-]\d{2})$/, '$1:00');
    // M365 timestamps have no offset; assume UTC.
    const hasOffset = /(?:Z|[+-]\d{2}:\d{2})$/.test(normalised);
    if (!hasOffset) {
        normalised += 'Z';
    }
    return Date.parse(normalised);
}

function isRuleActive(rule: EventRule, event_timestamp: string): boolean {
    const has_start = rule.start !== null;
    const has_end = rule.end !== null;

    if (!has_start && !has_end) {
        return true;
    }

    const event_ms = parseTimestampMs(event_timestamp);
    const start_ms = has_start ? parseTimestampMs(rule.start!) : Number.NEGATIVE_INFINITY;
    const end_ms = has_end ? parseTimestampMs(rule.end!) : Number.POSITIVE_INFINITY;

    return event_ms >= start_ms && event_ms <= end_ms;
}

/** The condition types an {@link EventRule} can carry; each one is also the name of the rule field that holds it. */
type ConditionType = 'country' | 'asn' | 'ip' | 'geo';

/**
 * What a {@link Classifier.runWithTrace} call records while it runs. Mutable by design: the rule
 * predicates and the predicate loop write into it as the classification proceeds.
 */
interface ClassifierTrace {
    matchedRuleIds: Set<string>;
    evaluatedPredicates: Partial<Record<PredicateKeys, boolean>>;
    predicateErrors: string[];
}

/**
 * The result of {@link Classifier.runWithTrace}: a normal classification plus the rule attribution and
 * evaluated predicates the monitoring event store persists at ingest.
 */
export interface ClassifierTraceResult {
    classification: Classification;
    /** Ids of the rules that decided the outcome; empty when no rule matched. */
    matchedRuleIds: string[];
    /** Only the predicates actually evaluated — an absent key means "not evaluated", stored as NULL. */
    evaluatedPredicates: Partial<Record<PredicateKeys, boolean>>;
    /**
     * The messages of the errors a predicate threw during this run; empty on a clean one.
     *
     * {@link Classifier.run} catches a throwing predicate, logs it and carries on with the branch treated as matched, so
     * the classification it returns for such an event is not a verdict anyone computed. `run()` keeps that behaviour for
     * its consumers; the trace reports it so the monitoring ingest can count the event as a classifier failure instead of
     * storing the accident as a grade (KRAK-393).
     */
    predicateErrors: string[];
}

/**
 * Classifies one transformed Microsoft audit event into a {@link Classification} (severity + display
 * text), applying the MSP's event rules.
 *
 * Two entry points, and the difference matters:
 *
 * - {@link Classifier.run} is the long-standing one. Its signature, its return type and its behaviour
 *   are fixed by three existing consumers — the ETL summary Lambda
 *   (`backend/src/etl-events/summary/summary.lambda.ts`), the legacy Angular Clarity frontend
 *   (`frontend/projects/angular-clarity/.../classification.service.ts`, which constructs this class in
 *   the browser) and this package's own fixture suites. Nothing about `run()` may change.
 * - {@link Classifier.runWithTrace} additionally reports WHICH rules decided the outcome and WHICH
 *   predicates were evaluated. The monitoring event store stores those on the event row at ingest
 *   (`matched_rule_ids`, the `pred_*` columns) because classification there is computed once and never
 *   re-evaluated — see docs/monitoring-event-store-spec.md §7.4 (rulings MES-2 / R2 / R4).
 *
 * Tracing is off unless `runWithTrace` turns it on, so `run()` pays nothing for it: the recording sites
 * are `this.trace?.…`, which is a no-op while `trace` is null.
 *
 * A rule's condition types combine ANY-OF (ruling, Richard, 2026-10-14, KRAK-453): each `isAllowed*` predicate asks
 * the rule about its own condition type alone, so a rule that names a country AND an ASN allows any event from that
 * country and, separately, any event from that ASN. Country, region and IP are one datum (the source IP) at nested
 * granularities, so a conjunction across them was degenerate; the conjunctive mode KRAK-318 added was retired.
 * Within one type values are any-of, and rules are OR'd against each other.
 */
export class Classifier extends Predicates {
    private tenant_rules: EventRule[] = [];
    private user_rules: Map<string, EventRule[]> = new Map();

    /**
     * Non-null only for the duration of a {@link Classifier.runWithTrace} call. The rule predicates and
     * the predicate loop write into it; `run()` never sets it, so it is null on every legacy path.
     */
    private trace: ClassifierTrace | null = null;

    /**
     * Microsoft 365 service IP ranges (parsed once at construction). An audit ClientIP inside
     * one of these is treated as benign Microsoft activity. Sourced from `microsoft.m365_ip_ranges`
     * at runtime (summary lambda / frontend); defaults to none when the caller does not supply them.
     */
    private m365_networks: ParsedNetwork[];

    /** Lowercased application ids of Microsoft first-party apps: the tenant's inventory plus the static floor. */
    private microsoft_app_ids: Set<string>;

    /** Lowercased service-principal object ids of the tenant's Microsoft first-party apps (the inventory). */
    private microsoft_object_ids: Set<string>;

    /** Lowercased application ids the MSP allows for the tenant. */
    private allowed_app_ids: Set<string>;

    /**
     * @param tenant_id the tenant
     * @param app_id Octiga's own application id, whose actions are never graded
     * @param users the tenant's users (only `userPrincipalName` is read)
     * @param domains the tenant's verified domains (only `id` is read)
     * @param rules the event rules targeting the tenant
     * @param trustedNetworks Microsoft 365 service CIDRs
     * @param workloadIdentities what is known about the applications acting in the tenant (KRAK-470). Omitted, an
     * application acting as itself stays a system event graded info, as every caller before KRAK-470 expects.
     */
    constructor(
        public readonly tenant_id: string,
        private app_id: string,
        private users: User[],
        private domains: Domain[],
        rules: EventRule[],
        trustedNetworks: string[] = [],
        private workloadIdentities?: WorkloadIdentityContext,
    ) {
        super();
        this.setRules(rules);
        this.m365_networks = parseNetworks(trustedNetworks);
        this.microsoft_app_ids = new Set(
            [...MICROSOFT_FIRST_PARTY_FLOOR, ...(workloadIdentities?.microsoftAppIds ?? [])].map((id) =>
                id.toLowerCase(),
            ),
        );
        this.microsoft_object_ids = new Set(
            [...(workloadIdentities?.microsoftObjectIds ?? [])].map((id) => id.toLowerCase()),
        );
        this.allowed_app_ids = new Set([...(workloadIdentities?.allowedAppIds ?? [])].map((id) => id.toLowerCase()));
    }

    /**
     * Sorts rules by scope ('user' or 'tenant')
     * user rules are mapped by user UPN for faster comparison against events
     * @param rules
     */
    private setRules(rules: EventRule[]) {
        for (const rule of rules) {
            const forTenant = rule.target_tenants.some((id) => id === this.tenant_id);

            if (forTenant) {
                this.tenant_rules.push(rule);
                continue;
            }

            const tenantUsers = rule.target_users.filter(({ tenant_id }) => tenant_id === this.tenant_id);

            for (const { upn } of tenantUsers) {
                const _upn = upn.toLowerCase();
                const previous = this.user_rules.get(_upn) ?? [];
                this.user_rules.set(_upn, [...previous, rule]);
            }
        }
    }

    public run(event: MicrosoftEvent): Classification {
        event = { ...event }; // make a copy to workaround readonly properties from ngrx

        if (event.user_id) {
            event.user_id = event.user_id.toLowerCase();
        }

        event.operation = event.operation.trim();

        // An application acting as itself in an Entra ID record (KRAK-470), when this classifier knows the tenant's
        // applications. Such a record reaches ONLY the branches that opt in with `isActorWorkloadIdentity`: the human
        // branches of every rule, and the rules that have no application branch at all, keep grading it `info`, as the
        // system-user gate always did. Microsoft's own services perform most of these records (policy, user and device
        // updates), and a blanket grade for any application action is exactly what the ticket rules out.
        const workloadIdentity = !!this.workloadIdentities && isWorkloadIdentityActor(event);

        if (isSystemUser(event.user_type) && !workloadIdentity) {
            // if it's a system event
            return DefaultClassification(event);
        }

        if (ClassificationNotDefined(event)) {
            // if we don't have a classification defined
            return DefaultClassification(event);
        }

        if (event.ip_address && !isIPPublic(event.ip_address)) {
            // isn't a public IP
            return DefaultClassification(event);
        }

        // TODO: not sure why we have this '2023-02-17' date check, but can probably be removed after 2024-02-17
        if (event.user_id?.includes(this.app_id) && event.timestamp.split('T')[0] >= '2023-02-17') {
            // octiga app performed events
            return DefaultClassification(event);
        }

        if (event.actor_app_id && event.actor_app_id.toLowerCase() === this.app_id.toLowerCase()) {
            // Octiga's own application acting app-only (`user_id` is then `serviceprincipal_<objectId>`, not the app id)
            return DefaultClassification(event);
        }

        if (event.user_id?.substring(0, 13) === 'urn:spo:anon#') {
            // sharepoint anon event
            // TODO: investigate the semantics of these spo events further
            return DefaultClassification(event);
        }

        // we have a definition for this operation
        const operation = workloads[event.workload][event.record_type]![event.operation];

        if (operation.requiresWorkloadIdentities && !this.workloadIdentities) {
            // Part of KRAK-470's grading, which a classifier without the application inventory — the legacy app's, the
            // summary run's for a v1 MSP — does not apply: the operation is undefined for it, as before KRAK-470.
            return DefaultClassification(event);
        }

        if (!operation.classifications) {
            // it doesn't have any classifications, just info
            // TODO: remove this construct in favour of a simple 'info' classification
            return {
                title: operation.title,
                description: operation.default.longDescription,
                longDescription:
                    operation.default.longDescription || `This "${event.operation}" event is considered low risk.`,
                severity: 'info',
            };
        }

        const fields = event.fields as any; // TODO: fix interfaces
        let found: Classification | null = null;

        for (let i = 0; i < operation.classifications.length; i++) {
            const clx = operation.classifications[i];
            let all_fns_true = true;
            let all_flds_match = true;

            if (workloadIdentity && clx.predicates?.isActorWorkloadIdentity !== true) {
                // not a branch written for an application actor: see the gate above
                continue;
            }

            // console.log(event.id, `${i}`, clx.description)

            // check predicate functions
            if (clx.predicates) {
                const fns = Object.entries(clx.predicates);
                try {
                    all_fns_true = fns.every(([fn_name, fn_result]) => {
                        const result = this[fn_name as PredicateKeys](event);
                        // console.log(event.id, `${i}`, fn_name, 'expected:', fn_result, 'actual:', result)
                        if (this.trace) {
                            // `every` short-circuits, so this records the predicates that actually DECIDED
                            // the outcome — not every predicate the branch names. That is the semantics the
                            // "why did this fire" UI wants (spec §6.2's pred_* columns: null = not evaluated).
                            this.trace.evaluatedPredicates[fn_name as PredicateKeys] = result;
                        }
                        return fn_result === result;
                    });
                } catch (err) {
                    console.error(err);
                    // Reported on the trace only: `run()` itself behaves exactly as it always has.
                    this.trace?.predicateErrors.push(err instanceof Error ? err.message : String(err));
                }
            }

            // console.log(event.id, `${i}`, all_fns_true ? 'Match' : 'No Match')

            // check fields
            if (clx.fields) {
                const flds = Object.entries(clx.fields);
                all_flds_match = flds.every(([fld_name, fld_result]) => {
                    return fld_result === undefined || fld_result.includes(fields?.[fld_name]);
                });
            }

            // check both predicates and fields are true
            if (all_fns_true && all_flds_match) {
                found = { ...clx, title: operation.title };
                break;
            }
        }

        if (found === null) {
            return DefaultClassification(event);
        } else {
            return found;
        }
    }

    /**
     * {@link Classifier.run}, plus the audit trail the monitoring event store stores on the event row.
     *
     * `classification` is deep-equal to what `run()` returns for the same event — this method sets a
     * recording flag, delegates, and clears it; it never changes a decision. What it adds:
     *
     * - `matchedRuleIds` — every {@link EventRule} whose predicate matched AND was active at the event's
     *   timestamp. Because the predicate loop short-circuits, these are the rules that DECIDED the
     *   outcome, which is what the Rules sheet's "what this rule hid" means.
     * - `evaluatedPredicates` — the predicate results actually computed, for the "why did this fire" UI.
     *   A predicate the short-circuit never reached is absent (stored as NULL, not false).
     *
     * The trace is cleared in a `finally`, so a throwing classification cannot leak recording state into
     * the next call on a warm container.
     */
    public runWithTrace(event: MicrosoftEvent): ClassifierTraceResult {
        this.trace = { matchedRuleIds: new Set<string>(), evaluatedPredicates: {}, predicateErrors: [] };

        try {
            const classification = this.run(event);

            return {
                classification,
                matchedRuleIds: [...this.trace.matchedRuleIds],
                evaluatedPredicates: this.trace.evaluatedPredicates,
                predicateErrors: this.trace.predicateErrors,
            };
        } finally {
            this.trace = null;
        }
    }

    /**
     * Whether the event satisfies one condition type of a rule: any one of the rule's values of that type matches.
     * A rule that carries no value of the type does not satisfy it. Callers have already returned for an event
     * with no reputation, so `event.reputation` is set here.
     */
    private conditionMet(rule: EventRule, event: MicrosoftEvent, type: ConditionType): boolean {
        switch (type) {
            case 'asn':
                return rule.asn.some((asn) => asn === event.reputation!.asn);
            case 'country':
                return rule.country.some((cc) => cc === event.reputation!.country_code);
            case 'geo':
                return rule.geo.some(({ center, radius }) =>
                    isPointInCircle(
                        { lat: event.reputation!.latitude, lng: event.reputation!.longitude },
                        center,
                        radius,
                    ),
                );
            case 'ip':
                return rule.ip.some((ip) => isIPInNetwork(event.ip_address!, ip));
        }
    }

    /**
     * Whether a rule allows the event along one condition type (the `isAllowed*` predicate asking), and records the
     * rule on the active trace when it does (spec §7.4 R2; a no-op while `trace` is null, which is every call
     * through the public `run()`).
     *
     * The rule's own condition of that type decides, alone (any-of across types, KRAK-453): a rule allows only the
     * types it carries, and only while it is active, and is recorded as matched when any one of its types matched.
     */
    private ruleAllows(rule: EventRule, event: MicrosoftEvent, type: ConditionType): boolean {
        const matched =
            rule[type].length > 0 && this.conditionMet(rule, event, type) && isRuleActive(rule, event.timestamp);

        if (matched) {
            this.trace?.matchedRuleIds.add(rule.id);
        }

        return matched;
    }

    protected isAllowedASN(event: MicrosoftEvent): boolean {
        const checkRule = (rule: EventRule, event: MicrosoftEvent) => this.ruleAllows(rule, event, 'asn');

        // no data to check
        if (event.reputation === null) {
            return true;
        }

        // check tenant level rules
        for (const rule of this.tenant_rules) {
            if (checkRule(rule, event)) {
                return true;
            }
        }

        // check user specfic rules
        if (event.user_id && this.user_rules.has(event.user_id)) {
            for (const rule of this.user_rules.get(event.user_id)!) {
                if (checkRule(rule, event)) {
                    return true;
                }
            }
        }

        return false;
    }

    protected isAllowedCountry(event: MicrosoftEvent): boolean {
        const checkRule = (rule: EventRule, event: MicrosoftEvent) => this.ruleAllows(rule, event, 'country');

        // no data to check
        if (event.reputation === null) {
            return true;
        }

        // check tenant level rules
        for (const rule of this.tenant_rules) {
            if (checkRule(rule, event)) {
                return true;
            }
        }

        // check user specfic rules
        if (event.user_id && this.user_rules.has(event.user_id)) {
            for (const rule of this.user_rules.get(event.user_id)!) {
                if (checkRule(rule, event)) {
                    return true;
                }
            }
        }

        return false;
    }

    protected isAllowedRegion(event: MicrosoftEvent): boolean {
        const checkRule = (rule: EventRule, event: MicrosoftEvent) => this.ruleAllows(rule, event, 'geo');

        // no data to check
        if (event.reputation === null) {
            return true;
        }

        // check tenant level rules
        for (const rule of this.tenant_rules) {
            if (checkRule(rule, event)) {
                return true;
            }
        }

        // check user specfic rules
        if (event.user_id && this.user_rules.has(event.user_id)) {
            for (const rule of this.user_rules.get(event.user_id)!) {
                if (checkRule(rule, event)) {
                    return true;
                }
            }
        }

        return false;
    }

    protected isAllowedIP(event: MicrosoftEvent): boolean {
        const checkRule = (rule: EventRule, event: MicrosoftEvent) => this.ruleAllows(rule, event, 'ip');

        // no data to check
        if (event.reputation === null) {
            return true;
        }

        // check tenant level rules
        for (const rule of this.tenant_rules) {
            if (checkRule(rule, event)) {
                return true;
            }
        }

        // check user specfic rules
        if (event.user_id && this.user_rules.has(event.user_id)) {
            for (const rule of this.user_rules.get(event.user_id)!) {
                if (checkRule(rule, event)) {
                    return true;
                }
            }
        }

        return false;
    }

    protected isFraudulentIP(event: MicrosoftEvent): boolean {
        return false;
        // if (event.ip_address === null)
        //     return false;
        // let ipqs = event.ipqs;
        // if (ipqs !== undefined) {
        //     const region_allowList = this.allowList.filter(item => (item.type === 'coord' && item.active));
        //     const country_allowList = this.allowList.filter(item => item.type === 'country');
        //     const foundRegion = region_allowList.find(item => {
        //         return isPointInCircle({ lat: ipqs!.latitude!, lng: ipqs!.longitude! }, { lat: item.center!.lat, lng: item.center!.lng }, item.radius!)
        //     })

        //     const foundCountry = country_allowList.find(item => item.country_code === ipqs!.country_code);

        //     if (foundRegion && foundRegion.bypass.fraudIP) {
        //         return false;
        //     }
        //     else if (foundCountry && foundCountry.bypass.fraudIP) {
        //         return false;
        //     }
        //     else if (ipqs && ipqs.fraud_score) { // ip_address.ipqs can be null if IPv6, etc.
        //         return ipqs.fraud_score > 75;
        //     }
        //     else {
        //         return false;
        //     }
        // }
        // else {
        //     return false;
        // }
    }

    protected isUserMemberOfOrganisation(event: MicrosoftEvent): boolean {
        let match = false;
        if (event.user_id) {
            const name = event.user_id;
            match = this.users.some((u) => u.userPrincipalName?.toLowerCase() === name.toLowerCase());
        }
        // TODO: do actual check
        return match;
    }

    /**
     * Whether an inbox rule sends mail to anyone outside the organisation: the test that grades a forwarding rule
     * Danger rather than Info when it was created from an approved location (KRAK-406).
     *
     * The rule is outside the organisation when ANY address in ANY of its ForwardTo, RedirectTo and
     * ForwardAsAttachmentTo values is. Each value is read by {@link readForwardingRecipients}:
     * - One value can hold several recipients joined by `;`, and every one is checked. Until 2026-10-11 only the text
     *   after the value's last `@` was read, so `outsider@gmail.com;colleague@ours.com` passed as internal and raised
     *   no alert.
     * - An address is found in each spelling the audit log uses: `a@b.com`, `SMTP:a@b.com` and OWA's
     *   `"Display Name" [SMTP:a@b.com]`, a comma in the display name included.
     * - Its domain is compared without regard to case, and without a trailing `.`, against the tenant's domains and
     *   the record's organisation name, normalised the same way ({@link normaliseDomain}). DNS names ignore case, so
     *   `Colleague@Ours.COM` is internal; compared as typed, it counted as external and raised a false Danger.
     * - An entry with no `@` at all is a directory reference — a display name, an alias, or a legacyExchangeDN
     *   (`/o=ExchangeLabs/…`, bare or as OWA's `"Name" [EX:/o=…]`). Exchange resolves such a value only against the
     *   tenant's own directory, so it is internal; taken whole as a domain, it matched nothing and raised a false
     *   Danger. (The directory object could be a mail contact whose address is outside; the audit record does not
     *   say, so this test cannot see it.)
     * - An entry with an `@` that belongs to no address that can be read cannot be shown to be internal, so it counts
     *   as outside. That covers an IP-literal domain, and also a quoted local part or a doubled `@`
     *   (`"ceo@ours.com"@evil.example`, `a@ours.com@evil.example`): read loosely, those yield only the inner,
     *   company-looking address and would hide the outside domain the mail goes to.
     * - The reading errs towards alerting. An `@` inside a quoted display name (`"Sales@HQ" [SMTP:sales@ours.com]`) is
     *   read as an address of its own, or leaves the entry unreadable, and a quoted local part with a space
     *   (`"john doe"@ours.com`) is unreadable, so such a rule grades Danger although every recipient is internal.
     *   Neither shape appears in the production sample of these values.
     * - A value of separators and spaces alone names no recipient, so it is not outside.
     *
     * A pure function of the event and the tenant's domains.
     *
     * @param event the transformed New-InboxRule or Set-InboxRule event
     * @returns true when any recipient is outside the organisation; false when none is, when the rule does not
     * forward, or when the record has no organisation name
     */
    protected isForwardedOutsideOfOrg(event: MicrosoftEvent): boolean {
        const fields = event.fields;
        if (!fields || !fields.forwarding_set || !fields.org) {
            return false;
        }

        const ownDomains = new Set(
            [...this.domains.map((domain) => domain.id), fields.org]
                .filter((domain): domain is string => typeof domain === 'string')
                .map(normaliseDomain),
        );

        return [fields.forward_to, fields.redirect_to, fields.forward_as_attachment_to].some(
            (value) =>
                typeof value === 'string' &&
                readForwardingRecipients(value).some((recipient) =>
                    recipient.kind === 'smtp'
                        ? recipient.domains.some((domain) => !ownDomains.has(domain))
                        : recipient.kind === 'unreadable',
                ),
        );
    }

    protected isMicrosoftActivity(event: MicrosoftEvent): boolean {
        if (this.isMicrosoftFirstPartyApp(event)) {
            return true;
        }

        const microsoft_upns = new Set(
            ['NT AUTHORITY\\SYSTEM (w3wp)', 'NT AUTHORITY\\SYSTEM (Microsoft.Exchange.AdminApi.NetCore)'].map((upn) =>
                upn.toLowerCase(),
            ),
        );

        if (event.user_id && microsoft_upns.has(event.user_id)) {
            return true;
        }

        // ClientIPs inside Microsoft's published 365 service ranges (endpoints.office.com) are
        // Microsoft 365 infrastructure — those prefixes are never allocated to Azure customer
        // workloads, so this is authoritative even when the geo provider mislabels the ISP.
        // Note the reverse is NOT true: a Microsoft ASN/ISP outside these ranges may be a
        // customer's (or attacker's) Azure VM and must not be treated as Microsoft activity.
        if (event.ip_address && isIpInNetworks(event.ip_address, this.m365_networks)) {
            return true;
        }

        const microsoft_isp_names = new Set(['Microsoft Corporation', 'Microsoft Azure', 'Microsoft']);

        if (event.reputation === null || !event.reputation.isp) {
            return false;
        }

        return microsoft_isp_names.has(event.reputation.isp);
    }

    /**
     * False whenever this classifier was built without an application inventory, whatever the event looks like: that
     * is what makes the KRAK-470 switch airtight, since every application branch names this predicate first.
     */
    protected isActorWorkloadIdentity(event: MicrosoftEvent): boolean {
        return !!this.workloadIdentities && isWorkloadIdentityActor(event);
    }

    /**
     * Whether the acting application is the principal the operation acted on: the role extractor's `targetObjectId`,
     * or the record's `ObjectId` (`ServicePrincipal_<objectId>` or the application id, as Microsoft spells it), against
     * the actor's object id or application id.
     */
    protected isSelfAssignment(event: MicrosoftEvent): boolean {
        const actor = actorObjectId(event);

        if (!actor) {
            return false;
        }

        const fields = (event.description_fields ?? {}) as { targetObjectId?: unknown };
        const target = typeof fields.targetObjectId === 'string' ? fields.targetObjectId.toLowerCase() : null;
        const objectId = event.object_id ? event.object_id.toLowerCase().replace(/^serviceprincipal_/, '') : null;

        const appId = event.actor_app_id?.toLowerCase();

        return target === actor || objectId === actor || (!!appId && objectId === appId);
    }

    /** Whether the role in the description fields is in the shared privileged-role list, by template id or by name. */
    protected isPrivilegedRole(event: MicrosoftEvent): boolean {
        const fields = (event.description_fields ?? {}) as { roleName?: unknown; roleTemplateId?: unknown };

        return (
            (typeof fields.roleTemplateId === 'string' &&
                PRIVILEGED_TEMPLATE_IDS.has(fields.roleTemplateId.toLowerCase())) ||
            (typeof fields.roleName === 'string' && PRIVILEGED_ROLE_NAMES.has(fields.roleName.toLowerCase()))
        );
    }

    /**
     * Whether the acting application is Microsoft first-party, by application id (the inventory or the static floor)
     * or by service-principal object id (the inventory), so it holds on the summary run's index too, which projects
     * `user_id` but not the actor fields.
     */
    protected isMicrosoftFirstPartyApp(event: MicrosoftEvent): boolean {
        const appId = event.actor_app_id?.toLowerCase();
        const objectId = actorObjectId(event);

        return (
            (!!appId && this.microsoft_app_ids.has(appId)) || (!!objectId && this.microsoft_object_ids.has(objectId))
        );
    }

    /** Whether the acting application is one the tenant expects: Microsoft first-party, or on the MSP's allow list. */
    protected isKnownVendorApp(event: MicrosoftEvent): boolean {
        return (
            this.isMicrosoftFirstPartyApp(event) ||
            (!!event.actor_app_id && this.allowed_app_ids.has(event.actor_app_id.toLowerCase()))
        );
    }
}
